import { StationApi, StationLotHistoryRow } from 'api/StationApi'
import clsx from 'clsx'
import TablePagination from 'components/TablePagination/TablePagination'
import { format } from 'date-fns'
import { useIsMobile } from 'hooks/useIsMobile'
import { ReactComponent as ErrorSvg } from 'images/icons/error.svg'
import { ReactComponent as OkSvg } from 'images/icons/ok.svg'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectStation } from 'store/selectors/station'
import { humanizeSnakeCaseLabel, maintenanceStageMap } from 'utils/lotInterfaces'
import { formatStationErrorMsg } from 'utils/stationErrorMsg'

import style from './EventsLog.module.css'

/** Matches backend StateHistoryEventType. */
const EVENT_OCCUPIED = 1
const EVENT_RELEASED = 2
const EVENT_STAGE = 3

/** occupied_status=RELEASED on Occupied event = Occupy failed (from occupy_response). */
const OCCUPIED_STATUS_RELEASED = 2

const STATUS_SUCCEEDED = 2
const STATUS_ABORTED = 3
const STATUS_CANCELED = 4
const STATUS_ERROR = 5

const PAGE_SIZE = 10

type HistoryRow = StationLotHistoryRow

const EventsLog: React.FC = () => {
  const isMobile = useIsMobile()
  const [events, setEvents] = React.useState<HistoryRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageInput, setPageInput] = useState('1')
  const [loading, setLoading] = useState(false)
  const station = useSelector(selectStation)
  const dateFormat = isMobile ? 'dd MMM yyyy' : 'dd LLLL yyyy'

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const response = await StationApi.fetchLotHistory(station.id, page, PAGE_SIZE)
        if (cancelled) return
        setEvents(response.results)
        setTotal(response.total)
      } catch (error) {
        console.warn('[EventsLog] fetchLotHistory failed', error)
        if (!cancelled) {
          setEvents([])
          setTotal(0)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [station.id, page])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages)
    }
  }, [page, totalPages])

  useEffect(() => {
    setPageInput(String(page))
  }, [page])

  useEffect(() => {
    setPage(1)
  }, [station.id])

  const handlePageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') {
      return
    }
    const val = parseInt(pageInput, 10)
    if (!Number.isNaN(val) && val >= 1 && val <= totalPages) {
      setPage(val)
    } else {
      setPageInput(String(page))
    }
  }

  const formatRobot = (robot_sn: string | number | null | undefined) => {
    if (robot_sn == null || robot_sn === '' || robot_sn === 0 || robot_sn === '0') {
      return '—'
    }
    return String(robot_sn)
  }

  const isOccupyFailed = (event: HistoryRow) =>
    event.event_type === EVENT_OCCUPIED && event.occupied_status === OCCUPIED_STATUS_RELEASED

  /** Browser tooltip for Occupy failed / stage errors (icon + Event cell). */
  const formatErrorTitle = (event: HistoryRow): string | undefined => {
    if (isOccupyFailed(event)) {
      const msg = formatStationErrorMsg(event.error_msg)
      const code = event.error_code
      if (msg && code != null) {
        return `code ${code}: ${msg}`
      }
      if (msg) {
        return msg
      }
      if (code != null) {
        return `code ${code}`
      }
      return 'Occupy failed'
    }
    if (event.event_type !== EVENT_STAGE) {
      return undefined
    }
    const status = event.state_status
    if (status !== STATUS_ABORTED && status !== STATUS_CANCELED && status !== STATUS_ERROR) {
      return undefined
    }
    return (
      formatStationErrorMsg(event.error_msg) ||
      (status === STATUS_ERROR ? 'Error' : status === STATUS_CANCELED ? 'Canceled' : 'Aborted')
    )
  }

  const formatEventLabel = (event: HistoryRow) => {
    if (event.event_type === EVENT_OCCUPIED) {
      // Failed occupy from occupy_response: Occupied + ERROR + occupied_status=RELEASED.
      // Successful Occupied keeps occupied_status=OCCUPIED (ignore live actuator ERROR).
      if (isOccupyFailed(event)) {
        return 'Occupy failed'
      }
      return 'Occupied'
    }
    if (event.event_type === EVENT_RELEASED) {
      return 'Released'
    }
    if (event.event_type === EVENT_STAGE) {
      const stage = event.maintenance_stage
      if (stage != null && maintenanceStageMap[stage]) {
        return maintenanceStageMap[stage]
      }
      if (stage != null) {
        return humanizeSnakeCaseLabel(String(stage))
      }
      return 'Stage'
    }
    return '—'
  }

  const renderOutcomeIcon = (event: HistoryRow) => {
    if (event.event_type === EVENT_OCCUPIED) {
      if (isOccupyFailed(event)) {
        const title = formatErrorTitle(event) || 'Occupy failed'
        return <ErrorSvg className={style.outcomeIcon} aria-label={title} title={title} />
      }
      return <OkSvg className={style.outcomeIcon} aria-label="Succeeded" />
    }
    if (event.event_type === EVENT_RELEASED) {
      return <OkSvg className={style.outcomeIcon} aria-label="Succeeded" />
    }
    if (event.event_type !== EVENT_STAGE) {
      return null
    }
    const status = event.state_status
    if (status === STATUS_SUCCEEDED) {
      return <OkSvg className={style.outcomeIcon} aria-label="Succeeded" />
    }
    if (status === STATUS_ABORTED || status === STATUS_CANCELED || status === STATUS_ERROR) {
      const title = formatErrorTitle(event) || 'Error'
      return <ErrorSvg className={style.outcomeIcon} aria-label={title} title={title} />
    }
    return null
  }

  if (!loading && total === 0) {
    return (
      <div className={style.tableWrapper}>
        <div className={style.empty}>No history events yet.</div>
      </div>
    )
  }

  return (
    <div className={style.tableWrapper}>
      <div className={style.tableScrollArea}>
        <div className={style.tableScrollContent}>
          <div className={clsx(style.tableRow, style.tableHead)}>
            <div className={clsx(style.tableCol, style.lotCol)}>Lot</div>
            <div className={clsx(style.tableCol, style.robotCol)}>Robot</div>
            <div className={clsx(style.tableCol, style.eventCol)}>Event</div>
            <div className={clsx(style.tableCol, style.outcomeCol)} aria-label="Outcome" />
            <div className={clsx(style.tableCol, style.dateCol)}>Date</div>
          </div>
          <div className={style.table}>
            {events.map(event => (
              <div key={`${event.lot_id}-${event.id}-${event.timestamp}`} className={style.tableRow}>
                <div className={clsx(style.tableCol, style.lotCol)}>
                  {event.lot_name?.trim() || `Lot ${event.lot_id}`}
                </div>
                <div className={clsx(style.tableCol, style.robotCol)}>{formatRobot(event.robot_sn)}</div>
                <div className={clsx(style.tableCol, style.eventCol)} title={formatErrorTitle(event)}>
                  {formatEventLabel(event)}
                </div>
                <div className={clsx(style.tableCol, style.outcomeCol)}>{renderOutcomeIcon(event)}</div>
                <div className={clsx(style.tableCol, style.dateCol)}>
                  <div>{event.timestamp ? format(new Date(event.timestamp), dateFormat) : '—'}</div>
                  <div className={style.timeLine}>
                    {event.timestamp ? format(new Date(event.timestamp), 'HH:mm:ss') : '—'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {total > PAGE_SIZE && (
        <div className={style.paginationFooter}>
          <TablePagination
            page={page}
            totalPages={totalPages}
            pageInput={pageInput}
            onPageInputChange={setPageInput}
            onPageInputKeyDown={handlePageJump}
            onPageInputBlur={() => setPageInput(String(page))}
            onPrev={() => setPage(prev => Math.max(prev - 1, 1))}
            onNext={() => setPage(prev => Math.min(prev + 1, totalPages))}
          />
        </div>
      )}
    </div>
  )
}

export default EventsLog
